import React, { memo, useCallback } from 'react';
import {
  Avatar,
  ChakraFlex,
  FAIconName,
  Icon,
  Text,
  Tooltip,
} from '@equip.health/ui';
import ProviderAvatarTooltipWrapper from '~/components/common/ProviderAvatarBlockTooltipWrapper';
import SelectableUserAvatarBlock from '../SelectableUserAvatarBlock';
import { getDisplayNameOrLegalNameForProvider } from '~/lib/util/user.util';
import BadgeIcon from '../BadgeIcon';

interface TimeSlotProps {
  id: string;
  isSelected?: boolean;
  onSelect: (slot: AppointmentSlot) => void;
  provider: GetProviderByIdResponse;
  slot: AppointmentSlot;
  patientAvailabilityPreferences: PatientAvailabilityPreferences | null;
  width?: string;
}

const AvatarTooltipWrapper = ({
  id,
  provider,
  children,
}: {
  id: string;
  provider: GetProviderByIdResponse;
  children: React.ReactNode;
}) => (
  <ProviderAvatarTooltipWrapper
    placement="top-start"
    providerExternalId={provider.userId}
    tooltipLabel={
      <SelectableUserAvatarBlock
        id={id}
        showPrimaryTag={true}
        user={provider}
        variant="compact"
      />
    }
  >
    {children}
  </ProviderAvatarTooltipWrapper>
);

const TimeSlot = ({
  id,
  isSelected,
  onSelect,
  patientAvailabilityPreferences,
  provider,
  slot,
  width,
}: TimeSlotProps) => {
  const selected = isSelected ?? slot.isSelected;

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onSelect(slot);
      }
    },
    [onSelect, slot],
  );

  const handleClick = useCallback(() => onSelect(slot), [onSelect, slot]);

  const tooltipWrapper = useCallback(
    (props: { children: React.ReactNode }) => (
      <AvatarTooltipWrapper id={id} provider={provider}>
        {props.children}
      </AvatarTooltipWrapper>
    ),
    [id, provider],
  );

  return (
    <ChakraFlex
      _hover={{
        backgroundColor: 'primary.10',
        borderColor: 'primary.100',
      }}
      alignItems="center"
      backgroundColor={selected ? 'primary.10' : 'white'}
      borderColor={selected ? 'primary.100' : 'neutral.line'}
      borderRadius="12px"
      borderWidth="1px"
      cursor="pointer"
      gap="12px"
      id={`${id}__time-slot-${slot.startTime}__provider-id-${slot.providerId}`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      padding="16px"
      position="relative"
      tabIndex={0}
      width={width || '240px'}
    >
      <ChakraFlex position="relative">
        <Avatar
          id={id}
          name={getDisplayNameOrLegalNameForProvider(
            provider.displayFirstName,
            provider.firstName,
            provider.displayLastName,
            provider.lastName,
          )}
          size="xs"
          src={provider.avatar?.thumbnailUrl}
          tooltipWrapper={tooltipWrapper}
          userType="provider"
        />
        {provider.primaryTag && (
          <ChakraFlex position="absolute" right="-8px" top="-8px">
            <BadgeIcon withBackground />
          </ChakraFlex>
        )}
      </ChakraFlex>
      <ChakraFlex
        alignItems="center"
        gap="12px"
        justifyContent="space-between"
        width="100%"
      >
        <Text id={`${id}__time`} variant="bodyMedium">
          {slot.formattedStartEndTime}
        </Text>
        {selected && (
          <Icon color="primary.100" name={FAIconName.checkRegular} />
        )}
        {!selected && slot.isPatientAvailabilityPreferenceMatch && (
          <Tooltip
            id={id}
            placement="top"
            textColor="black"
            tooltipText={`Matches patient's time preferences (${patientAvailabilityPreferences?.join(', ') || ''})`}
            width="186px"
          >
            <Icon color="accent.blue" name={FAIconName.clockRegular} />
          </Tooltip>
        )}
      </ChakraFlex>
    </ChakraFlex>
  );
};

export default memo(TimeSlot);
